import { techStackData } from "@/mocks/homeData";
import AnimatedSection from "@/components/AnimatedSection";

function TechItem({
  name,
  logo,
  featured,
  delay,
}: {
  name: string;
  logo: string;
  featured?: boolean;
  delay: number;
}) {
  return (
    <div
      className="animate-fade-in-up"
      style={{ animationDelay: `${delay}ms`, animationFillMode: "both" }}
    >
      <div
        className={`group flex flex-col items-center gap-2 p-2 md:p-2.5 rounded-xl border transition-all duration-300 cursor-default ${
          featured
            ? "border-accent-300/30 bg-accent-50/50 hover:bg-accent-50/80 hover:border-accent-400/40 hover:shadow-md hover:shadow-accent-500/10"
            : "border-transparent hover:border-accent-200/30 hover:bg-background-50 hover:shadow-sm"
        }`}
      >
        <img
          src={logo}
          alt={`${name} logo`}
          className={`object-contain transition-transform duration-300 group-hover:scale-110 ${
            featured
              ? "h-12 sm:h-[52px] md:h-14"
              : "h-10 sm:h-11 md:h-[52px]"
          }`}
          style={{ width: "auto", maxWidth: "110px" }}
          loading="lazy"
        />
        <span
          className={`text-[12px] md:text-[13px] font-semibold transition-colors duration-300 text-center leading-tight ${
            featured
              ? "text-accent-700"
              : "text-foreground-600 group-hover:text-accent-600"
          }`}
        >
          {name}
        </span>
      </div>
    </div>
  );
}

function CategoryBox({
  group,
  index,
}: {
  group: {
    name: string;
    description: string;
    icon: string;
    items: { name: string; logo: string; featured?: boolean }[];
  };
  index: number;
}) {
  const isAi = group.name === "AI & AI Development";

  return (
    <AnimatedSection delay={index * 80} className="h-full">
      <div
        className={`group relative h-full flex flex-col rounded-2xl border bg-background-50 p-5 md:p-6 transition-all duration-400 hover:-translate-y-1 ${
          isAi
            ? "border-accent-300/40 shadow-sm hover:shadow-md hover:shadow-accent-500/10"
            : "border-background-200/70 hover:border-accent-300/30 hover:shadow-md hover:shadow-accent-500/5"
        }`}
      >
        {/* AI special gradient border accent */}
        {isAi && (
          <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-accent-500 via-secondary-500 to-primary-500 rounded-t-2xl" />
        )}

        <div className="flex items-center gap-3 mb-2">
          <div
            className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 transition-colors duration-300 ${
              isAi
                ? "bg-accent-500/10 text-accent-600 group-hover:bg-accent-500 group-hover:text-background-50"
                : "bg-primary-50 text-primary-600 group-hover:bg-accent-500 group-hover:text-background-50"
            }`}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`${group.icon} text-lg`} />
            </span>
          </div>
          <div>
            <h3 className="text-sm md:text-base font-bold text-foreground-950 font-heading">
              {group.name}
            </h3>
          </div>
        </div>

        <p className="text-xs md:text-[13px] text-foreground-500 leading-relaxed mb-4">
          {group.description}
        </p>

        {/* Technology logo grid - flex-1 lets it expand so every box matches the tallest */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 md:gap-2.5 flex-1 content-center">
          {group.items.map((item, ii) => (
            <TechItem
              key={item.name}
              name={item.name}
              logo={item.logo}
              featured={item.featured}
              delay={ii * 60}
            />
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

export default function TechStackSection() {
  return (
    <section id="technologies" className="py-20 md:py-28 bg-background-50">
      <div className="container-astro">
        <AnimatedSection>
          <div className="text-center mb-14 md:mb-18">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-4 font-heading tracking-tight">
              {techStackData.heading}
            </h2>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              {techStackData.subtext}
            </p>
            <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto mt-5" />
          </div>
        </AnimatedSection>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6 auto-rows-fr">
          {techStackData.groups.map((group, i) => (
            <CategoryBox key={group.name} group={group} index={i} />
          ))}
        </div>

        {/* Section CTA */}
        <AnimatedSection delay={400}>
          <div className="text-center mt-14 md:mt-18">
            <p className="text-sm md:text-base text-foreground-500 mb-5">
              Looking for the right technology for your project?
            </p>
            <a
              href="#contact"
              className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-[#2563EB]/20 text-[#2563EB] text-sm font-semibold rounded-xl hover:bg-[#2563EB]/5 hover:border-[#2563EB]/40 hover:-translate-y-0.5 transition-all duration-300 whitespace-nowrap btn-arrow-hover group"
            >
              Talk to Our Experts
              <i className="ri-arrow-right-line btn-arrow transition-transform duration-300 group-hover:translate-x-1" />
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
}